// ============================
// File: src/features/aoa/mockAoa.js
// ============================
//
// Mock data for the Analysis of Alternatives (AoA) stage.
//
// There is no AoA backend yet. Everything here is demonstration data built from
// one reference case (IT service desk modernization) and varied deterministically
// per demandId, so two demands look different but the same demand always shows
// the same numbers across reloads. Nothing in this file is a real estimate.

/** Evaluation criteria. Every score is 1–5 where 5 is always "better". */
export const CRITERIA = [
  { id: "mission", label: "Mission effectiveness", short: "Mission",
    help: "How fully the option meets the stated need and mission outcomes." },
  { id: "cost", label: "Cost", short: "Cost",
    help: "5-year lifecycle cost relative to the other options (5 = cheapest)." },
  { id: "risk", label: "Risk", short: "Risk",
    help: "Delivery, operational and vendor risk (5 = lowest risk)." },
  { id: "schedule", label: "Schedule", short: "Schedule",
    help: "Time to initial and full operating capability (5 = fastest)." },
  { id: "feasibility", label: "Technical feasibility", short: "Feasibility",
    help: "Maturity of the technology and fit with the current architecture." },
  { id: "security", label: "Security / compliance (FedRAMP, ATO)", short: "Security",
    help: "FedRAMP authorization status, ATO path and control inheritance." },
];

/** Default weights (percent). The UI normalises, so they need not sum to 100. */
export const DEFAULT_WEIGHTS = {
  mission: 30, cost: 20, risk: 15, schedule: 10, feasibility: 10, security: 15,
};

/** Real discount rate for NPV, in the spirit of OMB Circular A-94. */
export const DISCOUNT_RATE = 0.03;
export const HORIZON_YEARS = 5;

// Reference alternatives. Costs in $M. annualBenefit is non-O&M value
// (productivity, cost avoidance) relative to the status quo.
const BASE_ALTERNATIVES = [
  {
    id: "opt1",
    name: "Maintain Current State",
    short: "Status quo",
    description: "Continue operating the existing on-premises service desk tooling and staffing model with no material investment beyond routine sustainment.",
    oneTime: 0.0,
    annualOM: 2.9,
    annualBenefit: 0.0,
    ioc: 0,
    foc: 0,
    missionFit: "Meets today's baseline only. Does not address growing ticket backlog, self-service demand, or the end-of-support date on the current ticketing platform.",
    scores: { mission: 2, risk: 3, schedule: 5, feasibility: 5, security: 3 },
    risks: [
      "Current ticketing platform reaches vendor end-of-support within the analysis period",
      "Rising contractor labor rates drive O&M growth above inflation",
      "Backlog and SLA misses continue to erode customer satisfaction",
    ],
    assumptions: [
      "Staffing held flat at current levels",
      "No change to existing ATO boundary",
    ],
  },
  {
    id: "opt2",
    name: "Modernize Existing Platform",
    short: "Modernize",
    description: "Upgrade the current service desk platform to its supported release and automate selected high-volume processes (password resets, account provisioning, routine software requests).",
    oneTime: 2.4,
    annualOM: 2.3,
    annualBenefit: 0.5,
    ioc: 6,
    foc: 14,
    missionFit: "Partially meets the need. Removes the end-of-support exposure and automates the top ticket categories, but leaves the fragmented ITSM tool landscape in place.",
    scores: { mission: 3, risk: 4, schedule: 4, feasibility: 4, security: 4 },
    risks: [
      "Customizations from prior releases may not survive the upgrade",
      "Automation gains limited by legacy integration points",
      "Vendor roadmap for the on-premises edition is uncertain",
    ],
    assumptions: [
      "Upgrade covered by existing license entitlement",
      "Existing ATO amended rather than reissued",
    ],
  },
  {
    id: "opt3",
    name: "Implement ServiceNow",
    short: "ServiceNow",
    description: "Consolidate incident, request, change, problem and asset management onto ServiceNow (FedRAMP High authorized government community cloud), retiring three legacy ITSM tools.",
    oneTime: 3.8,
    annualOM: 1.9,
    annualBenefit: 0.9,
    ioc: 9,
    foc: 18,
    missionFit: "Strongly meets the need. Single system of record for IT services, mature self-service portal, and a clear path to enterprise service management beyond IT.",
    scores: { mission: 4, risk: 4, schedule: 3, feasibility: 4, security: 5 },
    risks: [
      "Data migration from three legacy tools and historical tickets",
      "Organizational change management across regional help desks",
      "License true-up exposure if fulfiller counts grow",
    ],
    assumptions: [
      "Agency-inherited controls from the FedRAMP High package",
      "Implementation partner procured on an existing BPA",
      "Legacy tools decommissioned by month 20",
    ],
  },
  {
    id: "opt4",
    name: "AI-Enabled Service Desk",
    short: "AI-enabled",
    description: "Deploy AI agents for tier-0/tier-1 triage, knowledge retrieval and automated fulfillment on top of a modern ITSM core, targeting a 35–45% reduction in manually handled tickets.",
    oneTime: 3.1,
    annualOM: 1.5,
    annualBenefit: 1.4,
    ioc: 8,
    foc: 20,
    missionFit: "Best mission outcome if it performs as projected: faster resolution, 24×7 coverage, and analysts redeployed to higher-value work. Depends on knowledge base quality.",
    scores: { mission: 5, risk: 2, schedule: 3, feasibility: 3, security: 2 },
    risks: [
      "AI components not yet covered by a FedRAMP authorization at the required impact level",
      "Requires an M-25-21 AI use case inventory entry and impact assessment",
      "Projected deflection rates are vendor estimates, not agency-measured",
      "Model drift and hallucination risk in user-facing answers",
    ],
    assumptions: [
      "Knowledge base curated to at least 80% coverage of top ticket types before IOC",
      "Human-in-the-loop for any action that changes access or entitlements",
      "Savings realized from attrition, not reduction in force",
    ],
  },
  {
    id: "opt5",
    name: "Managed Service",
    short: "Managed service",
    description: "Outsource service desk operations, including tooling and staffing, to a commercial provider under a performance-based contract with SLA incentives.",
    oneTime: 0.9,
    annualOM: 2.4,
    annualBenefit: 0.4,
    ioc: 5,
    foc: 10,
    missionFit: "Meets the need on paper with SLA guarantees, but reduces in-house control over the IT service experience and creates long-term vendor dependence.",
    scores: { mission: 3, risk: 3, schedule: 4, feasibility: 4, security: 3 },
    risks: [
      "Provider tooling must hold its own FedRAMP authorization and ATO",
      "Loss of institutional knowledge during transition",
      "Contract protest or recompete risk extends the schedule",
    ],
    assumptions: [
      "Award under an existing GWAC within 4 months",
      "Government retains data ownership and exit rights",
    ],
  },
];

/* ------------------------------------------------------------ seeding */

function hashString(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** mulberry32 — tiny deterministic PRNG. */
function seededRandom(seed) {
  let a = seed >>> 0;
  return function rand() {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const round1 = (v) => Math.round(v * 10) / 10;
const clampScore = (v) => Math.max(1, Math.min(5, Math.round(v)));

/* ------------------------------------------------------------ finance */

/** 5-year lifecycle cost ($M): one-time plus annual O&M over the horizon. */
export function lifecycleCost(alt) {
  return round1(alt.oneTime + alt.annualOM * HORIZON_YEARS);
}

/**
 * NPV ($M) of the option's net savings versus the status quo.
 * Year 0: −one-time. Years 1..N: (status-quo O&M − option O&M) + benefit,
 * with benefits ramping in at 50% in year 1.
 */
export function npvVsStatusQuo(alt, statusQuo) {
  let npv = -(alt.oneTime - statusQuo.oneTime);
  for (let y = 1; y <= HORIZON_YEARS; y += 1) {
    const benefit = alt.annualBenefit * (y === 1 ? 0.5 : 1);
    const flow = statusQuo.annualOM - alt.annualOM + benefit;
    npv += flow / (1 + DISCOUNT_RATE) ** y;
  }
  return round1(npv);
}

export function riskLevel(riskScore) {
  if (riskScore >= 4) return "Low";
  if (riskScore >= 3) return "Moderate";
  return "High";
}

/* ------------------------------------------------------------ builder */

/**
 * Build the mock AoA for a demand. Deterministic per demandId: costs vary ±15%,
 * benefits ±20%, schedules ±2 months, and each non-cost score has a small chance
 * of moving one point.
 */
export function buildMockAoa(demandId) {
  const rand = seededRandom(hashString(String(demandId || "DEMO")));
  const vary = (v, pct) => v * (1 + (rand() * 2 - 1) * pct);

  const alternatives = BASE_ALTERNATIVES.map((base) => {
    const oneTime = round1(Math.max(0, vary(base.oneTime, 0.15)));
    const annualOM = round1(vary(base.annualOM, 0.15));
    const annualBenefit = round1(Math.max(0, vary(base.annualBenefit, 0.2)));
    const shift = Math.round(rand() * 4 - 2);
    const ioc = base.ioc ? Math.max(3, base.ioc + shift) : 0;
    const foc = base.foc ? Math.max(ioc + 3, base.foc + shift + Math.round(rand() * 2)) : 0;
    const scores = { ...base.scores };
    Object.keys(scores).forEach((k) => {
      const r = rand();
      if (r < 0.15) scores[k] = clampScore(scores[k] - 1);
      else if (r > 0.85) scores[k] = clampScore(scores[k] + 1);
    });
    // The status quo never improves on mission or risk by chance — that would
    // contradict its own narrative (end-of-support, growing backlog).
    if (base.id === "opt1") {
      scores.mission = Math.min(scores.mission, 2);
      scores.risk = Math.min(scores.risk, 3);
    }
    return { ...base, oneTime, annualOM, annualBenefit, ioc, foc, scores };
  });

  // Cost score is derived from the computed lifecycle cost, not seeded, so the
  // matrix can never disagree with the cost chart.
  const costs = alternatives.map(lifecycleCost);
  const max = Math.max(...costs);
  const min = Math.min(...costs);
  const statusQuo = alternatives[0];

  const enriched = alternatives.map((alt, i) => {
    const fiveYearCost = costs[i];
    const costScore = max === min ? 3 : clampScore(1 + (4 * (max - fiveYearCost)) / (max - min));
    const scores = { ...alt.scores, cost: costScore };
    return {
      ...alt,
      scores,
      fiveYearCost,
      fiveYearBenefit: round1(alt.annualBenefit * (HORIZON_YEARS - 0.5)),
      npvVsStatusQuo: alt.id === statusQuo.id ? 0 : npvVsStatusQuo(alt, statusQuo),
      riskLevel: riskLevel(scores.risk),
    };
  });

  return {
    demandId,
    preparedOn: "Mock analysis — not an agency estimate",
    discountRate: DISCOUNT_RATE,
    horizonYears: HORIZON_YEARS,
    alternatives: enriched,
  };
}

/* ------------------------------------------------------------ scoring */

/** Weighted score (0–5) for each alternative, sorted best first with rank. */
export function rankAlternatives(alternatives, weights) {
  const total = CRITERIA.reduce((s, c) => s + (Number(weights[c.id]) || 0), 0);
  const scored = alternatives.map((alt) => {
    const weighted = total
      ? CRITERIA.reduce((s, c) => s + alt.scores[c.id] * (Number(weights[c.id]) || 0), 0) / total
      : 0;
    return { ...alt, weighted: Math.round(weighted * 100) / 100 };
  });
  const order = [...scored].sort((a, b) => b.weighted - a.weighted || a.fiveYearCost - b.fiveYearCost);
  const rankById = Object.fromEntries(order.map((a, i) => [a.id, i + 1]));
  return scored.map((a) => ({ ...a, rank: rankById[a.id] })).sort((a, b) => a.rank - b.rank);
}

export const fmtM = (v) => `$${Number(v ?? 0).toFixed(1)}M`;
export const fmtSignedM = (v) => `${v >= 0 ? "+" : "−"}$${Math.abs(Number(v ?? 0)).toFixed(1)}M`;
